import type { Client, Contract } from '../types'

export type SyncModuleKey = 'headshot' | 'contract' | 'stats' | 'incentives'
export type SyncModuleState = 'queued' | 'synced' | 'partial' | 'failed'
export type SyncState = 'queued' | 'synced' | 'partial' | 'failed'
export type FreshnessLabel = 'Live' | '24h old' | 'Stale'

export type SyncModuleStatus = {
  state: SyncModuleState
  attempts: number
  source?: string
  last_attempt_at?: string
  last_success_at?: string
  error?: string
  next_retry_at?: string
}

export type SyncDeadLetter = {
  module: SyncModuleKey
  error: string
  attempts: number
  last_attempt_at: string
}

export type ClientSyncContract = {
  state: SyncState
  queued_at: string
  updated_at: string
  modules: Record<SyncModuleKey, SyncModuleStatus>
  dead_letters: SyncDeadLetter[]
}

const MODULES: SyncModuleKey[] = ['headshot', 'contract', 'stats', 'incentives']

function nowIso(): string {
  return new Date().toISOString()
}

function defaultModuleStatus(): SyncModuleStatus {
  return {
    state: 'queued',
    attempts: 0,
  }
}

export function getDefaultSyncContract(): ClientSyncContract {
  const now = nowIso()
  return {
    state: 'queued',
    queued_at: now,
    updated_at: now,
    modules: {
      headshot: defaultModuleStatus(),
      contract: defaultModuleStatus(),
      stats: defaultModuleStatus(),
      incentives: defaultModuleStatus(),
    },
    dead_letters: [],
  }
}

export function parseClientSyncContract(externalIds: Record<string, any> | null | undefined): ClientSyncContract {
  const raw = (externalIds && typeof externalIds === 'object' ? externalIds.sync_contract : null) || {}
  const fallback = getDefaultSyncContract()
  const modules: Record<SyncModuleKey, SyncModuleStatus> = {
    headshot: { ...fallback.modules.headshot, ...(raw.modules?.headshot || {}) },
    contract: { ...fallback.modules.contract, ...(raw.modules?.contract || {}) },
    stats: { ...fallback.modules.stats, ...(raw.modules?.stats || {}) },
    incentives: { ...fallback.modules.incentives, ...(raw.modules?.incentives || {}) },
  }

  return {
    ...fallback,
    ...raw,
    modules,
    dead_letters: Array.isArray(raw.dead_letters) ? raw.dead_letters : [],
  }
}

export function initializeSyncContract(
  externalIds: Record<string, any> | null | undefined,
  onboarding?: Record<string, any>
): Record<string, any> {
  const sync = parseClientSyncContract(externalIds)
  const now = nowIso()
  return {
    ...(externalIds || {}),
    sync_contract: {
      ...sync,
      state: 'queued',
      queued_at: sync.queued_at || now,
      updated_at: now,
    },
    ...(onboarding ? { onboarding } : {}),
  }
}

export function updateModuleQueued(
  externalIds: Record<string, any> | null | undefined,
  module: SyncModuleKey,
  source?: string
): Record<string, any> {
  const sync = parseClientSyncContract(externalIds)
  const now = nowIso()
  sync.modules[module] = {
    ...sync.modules[module],
    state: 'queued',
    source: source || sync.modules[module].source,
    last_attempt_at: now,
    error: undefined,
  }
  sync.updated_at = now
  sync.state = deriveSyncState(sync)
  return {
    ...(externalIds || {}),
    sync_contract: sync,
  }
}

export function updateModuleSuccess(
  externalIds: Record<string, any> | null | undefined,
  module: SyncModuleKey,
  source?: string
): Record<string, any> {
  const sync = parseClientSyncContract(externalIds)
  const now = nowIso()
  sync.modules[module] = {
    ...sync.modules[module],
    state: 'synced',
    source: source || sync.modules[module].source,
    last_attempt_at: now,
    last_success_at: now,
    error: undefined,
    next_retry_at: undefined,
  }
  sync.updated_at = now
  sync.state = deriveSyncState(sync)
  return {
    ...(externalIds || {}),
    sync_contract: sync,
  }
}

export function updateModuleFailed(
  externalIds: Record<string, any> | null | undefined,
  module: SyncModuleKey,
  error: string,
  maxRetries = 3
): Record<string, any> {
  const sync = parseClientSyncContract(externalIds)
  const now = nowIso()
  const prev = sync.modules[module] || defaultModuleStatus()
  const attempts = (prev.attempts || 0) + 1
  const exceeded = attempts >= maxRetries
  const nextRetryAt = exceeded ? undefined : new Date(Date.now() + attempts * 5 * 60 * 1000).toISOString()

  sync.modules[module] = {
    ...prev,
    state: exceeded ? 'failed' : 'partial',
    attempts,
    error,
    last_attempt_at: now,
    next_retry_at: nextRetryAt,
  }

  if (exceeded) {
    sync.dead_letters = [
      ...(sync.dead_letters || []).filter((d: SyncDeadLetter) => d.module !== module),
      { module, error, attempts, last_attempt_at: now },
    ]
  }

  sync.updated_at = now
  sync.state = deriveSyncState(sync)
  return {
    ...(externalIds || {}),
    sync_contract: sync,
  }
}

export function deriveSyncState(sync: ClientSyncContract): SyncState {
  const states = MODULES.map((m) => sync.modules[m]?.state || 'queued')
  if (states.every((s) => s === 'synced')) return 'synced'
  if (states.some((s) => s === 'failed')) return 'failed'
  if (states.some((s) => s === 'partial')) return 'partial'
  return 'queued'
}

export function getClientMissingModules(client: Client, contracts: Contract[]): SyncModuleKey[] {
  const missing: SyncModuleKey[] = []
  const hasContract = contracts.length > 0
  const hasIncentives = contracts.some((c) => Array.isArray(c.incentives) && c.incentives.length > 0)

  if (!client.headshot_url) missing.push('headshot')
  if (!hasContract) missing.push('contract')
  if (!client.stats_summary || Object.keys(client.stats_summary || {}).length === 0) missing.push('stats')
  if (!hasIncentives) missing.push('incentives')

  return missing
}

export function getFreshnessLabel(timestamp?: string | null): FreshnessLabel {
  if (!timestamp) return 'Stale'
  const ageMs = Date.now() - new Date(timestamp).getTime()
  if (ageMs <= 6 * 60 * 60 * 1000) return 'Live'
  if (ageMs <= 24 * 60 * 60 * 1000) return '24h old'
  return 'Stale'
}
